import { parseCustomDate } from '@/helpers/dateUtils'
import { isListingBuyNowActive } from '@/helpers/listingBuyNowActive'
import type { SearchResult } from '@/queries/listingSearchQuery'

/**
 * Price displayed on Grid View cards, and the basis for their % of MSRP and
 * price-per-unit values.
 *
 * Prefers live auction-service data over the search index's denormalized
 * `winningBidAmount`, which holds the effective Buy Now price during a Buy
 * Now window and lags ~2-3 minutes behind the Buy Now -> auction transition.
 * Analogous to List View's Buy Now gating
 * (CombinedListingSearchResult.priceForListDerivedMetrics).
 */
export const getGridCardPrice = (
  listing: SearchResult,
  auction: NonNullable<SearchResult['auction3MP']>
): number => {
  // Mirrors CombinedListingSearchResult.endTime (the auction end - NOT
  // getEndTime, which substitutes buyNowEndTime while the window is active)
  const endTime = parseCustomDate(auction.actualEndTime ?? listing.endTime)
  const buyNowEndTime = listing.buyNowEndTime
    ? parseCustomDate(listing.buyNowEndTime)
    : null

  // The auctions list endpoint is tiered by authentication: unauthenticated callers get
  // payloads that omit winningBidAmount and numberOfBids but keep buyNow
  // (authenticated buyers receive all three). Live data is authoritative; the
  // search doc's denormalized fields are the fallback for those omitted fields.

  if (
    isListingBuyNowActive({ buyNow: listing.buyNow, buyNowEndTime, endTime })
  ) {
    return auction.buyNow?.price ?? 0
  }

  // Unauthenticated payloads omit auction.numberOfBids; the search doc always carries it
  const numberOfBids = auction.numberOfBids ?? listing.numberOfBids
  if (numberOfBids > 0) {
    return (
      auction.winningBidAmount ||
      Number(listing.winningBidAmount) ||
      auction.startPrice ||
      0
    )
  }

  // No bids: live startPrice first; the search-doc value is stale-prone across
  // the Buy Now -> auction transition, so it is a last resort only
  return auction.startPrice || Number(listing.winningBidAmount) || 0
}
